import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowLeft, BookOpen, Camera, CalendarDays, Download, Leaf, Loader2, Luggage, Pencil, Play, Printer, RefreshCw, Route, Sparkles, Trash2, Undo2, Upload, Wallet, X, Zap } from "lucide-react";
import { co2SavedKg, co2Trees } from "../lib/co2";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "../lib/api";
import { confirmDialog } from "../components/ConfirmHost";
import { TripMap } from "../components/TripMap";
import { ImportantInfo } from "../components/ImportantInfo";
import { CurrencyConverter } from "../components/CurrencyConverter";
import { ChargeCalcDialog } from "../components/ChargeCalculator";
import { Calculator } from "lucide-react";
import { usePageMeta } from "../lib/seo";
import { Timeline } from "../components/Timeline";
import { PackingList } from "../components/PackingList";
import { AssistantChat, ImpactChip } from "../components/AssistantChat";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { LiveMode } from "../components/LiveMode";
import { BudgetTab } from "../components/BudgetTab";
import { CostRecap } from "../components/CostRecap";
import { PrintOrderButton, orderPrintBook } from "../components/PrintOrderButton";
import { SplitCosts } from "../components/SplitCosts";
import { GpxPreviewDialog } from "../components/GpxPreviewDialog";
import { TripStory } from "../components/TripStory";
import { MembersDialog } from "../components/MembersDialog";
import { VehicleCard } from "../components/VehicleCard";
import { BatteryStartCard } from "../components/BatteryStartCard";
import { ShareDialog } from "../components/ShareDialog";
import { PosterDialog } from "../components/PosterDialog";
import { ThemeToggle } from "../components/ThemeToggle";
import { GuestSuggestions } from "../components/GuestSuggestions";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const GENERATION_STEPS = [
  "Locaties opzoeken...",
  "Route berekenen...",
  "Laadstops plannen op basis van je batterij...",
  "Echte laadpunten zoeken via OpenChargeMap...",
  "Overnachtingen en activiteiten genereren met AI...",
  "Paklijst samenstellen...",
];

const GeneratingScreen = ({ trip, onCancel }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setStepIdx((i) => Math.min(i + 1, GENERATION_STEPS.length - 1)), 9000);
    return () => clearInterval(t);
  }, []);
  const prog = trip?.generation_progress;
  const step = prog?.step || GENERATION_STEPS[stepIdx];
  const pct = prog?.pct ?? Math.min(10 + stepIdx * 15, 85);
  const cancel = async () => {
    setCancelling(true);
    try { await onCancel?.(); } finally { setCancelling(false); }
  };
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center" data-testid="trip-generating-screen">
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2.5, ease: "linear" }}
        className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary mb-8" />
      <h2 className="font-heading font-extrabold text-2xl tracking-tight mb-3">Je trip wordt samengesteld</h2>
      <motion.p key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="text-muted-foreground" data-testid="generation-step-text">
        {step}
      </motion.p>
      <div className="w-64 h-2 bg-secondary rounded-full overflow-hidden mt-6" data-testid="generation-progress-bar">
        <div className="h-full bg-primary rounded-full transition-[width] duration-700 ease-out" style={{ width: `${Math.min(Math.max(pct, 3), 100)}%` }} />
      </div>
      <p className="text-xs text-muted-foreground mt-4">Dit duurt meestal 1 à 2 minuten.</p>
      {onCancel && (
        <Button data-testid="cancel-generation-button" variant="outline" size="sm" disabled={cancelling} onClick={cancel}
          className="mt-6 rounded-full h-9 px-5 text-xs">
          <X className="w-3.5 h-3.5 mr-1.5" /> {cancelling ? "Annuleren..." : "Berekening annuleren"}
        </Button>
      )}
    </div>
  );
};

const ReplanSummaryBanner = ({ trip, onRefresh }) => {
  const sum = trip.last_replan_summary;
  const key = sum ? `vr-replansum-${trip.id}-${sum.generated_at}` : null;
  const [hidden, setHidden] = useState(true);
  const [impact, setImpact] = useState(null);
  useEffect(() => { setHidden(key ? !!localStorage.getItem(key) : true); }, [key]);
  useEffect(() => {
    if (!key || hidden) return;
    api.get(`/trips/${trip.id}/plan-impact`)
      .then((r) => setImpact(r.data.status === "ready" ? r.data : null))
      .catch(() => setImpact(null));
  }, [key, hidden, trip.id]);
  if (!sum || hidden || !(sum.parts || []).length) return null;
  const dismiss = () => { localStorage.setItem(key, "1"); setHidden(true); };
  const undo = async () => {
    if (!(await confirmDialog("Herberekening ongedaan maken en de vorige planning terugzetten?", { title: "Ongedaan maken", confirmLabel: "Terugzetten" }))) return;
    try {
      const { data } = await api.post(`/trips/${trip.id}/restore-plan`);
      toast.success(`Vorige planning teruggezet (${data.days} dagen)`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <div data-testid="replan-summary-banner" className="mb-4 rounded-2xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
      <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">Planning herberekend</p>
        <p data-testid="replan-summary-text" className="text-xs text-muted-foreground mt-1">{sum.parts.join(" · ")}</p>
        {impact?.delta && (
          <div data-testid="replan-impact" className="mt-2 flex flex-wrap gap-1.5">
            <ImpactChip icon={Route} label="km" value={impact.new.km.toLocaleString("nl-NL")} delta={impact.delta.km} testId="impact-km" />
            <ImpactChip icon={Zap} label={impact.new.charge_stops === 1 ? "laadstop" : "laadstops"} value={impact.new.charge_stops} delta={impact.delta.charge_stops} testId="impact-stops" />
            <ImpactChip icon={CalendarDays} label={impact.new.days === 1 ? "dag" : "dagen"} value={impact.new.days} delta={impact.delta.days} testId="impact-days" />
          </div>
        )}
        <div className="flex gap-2 mt-2.5">
          <Button data-testid="replan-summary-undo" size="sm" variant="outline" className="rounded-full h-8 px-4 text-xs" onClick={undo}>
            <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Ongedaan maken
          </Button>
          <Button data-testid="replan-summary-ok" size="sm" className="rounded-full h-8 px-4 text-xs" onClick={dismiss}>OK, prima</Button>
        </div>
      </div>
      <button data-testid="replan-summary-dismiss" onClick={dismiss} className="p-1 rounded-full hover:bg-secondary transition-[background-color] duration-200">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [liveMode, setLiveMode] = useState(false);
  const [fixValue, setFixValue] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [progress, setProgress] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [routeHealth, setRouteHealth] = useState(null);
  const [healthDismissed, setHealthDismissed] = useState(false);
  const [countryData, setCountryData] = useState(null);
  usePageMeta(trip?.name ? `${trip.name} — VoltRoute` : "VoltRoute");

  useEffect(() => {
    if (!trip?.route?.geometry?.length || !["ready", "active", "completed"].includes(trip.status)) return;
    api.get(`/trips/${id}/countries`).then((r) => setCountryData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, trip?.route?.geometry?.length]);

  useEffect(() => {
    if (!trip || trip.is_owner === false || !["ready", "active"].includes(trip.status)) { setRouteHealth(null); return; }
    api.get(`/trips/${id}/route-health`).then((r) => setRouteHealth(r.data.issues || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, trip?.status, trip?.days?.length]);

  const fixRouteHealth = async () => {
    if (!(await confirmDialog("De assistent verwijdert de dagen die niet in de route thuishoren en herberekent de planning (je vorige planning wordt automatisch bewaard als backup). Doorgaan?", { title: "Route herstellen", confirmLabel: "Herstel route" }))) return;
    try {
      await api.post(`/trips/${id}/route-health/fix`);
      toast.success("Route wordt hersteld — planning wordt opnieuw berekend");
      setRouteHealth([]);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (trip?.status === "error") {
      const m = (trip.generation_error || "").match(/'(.+?)' niet gevonden/);
      if (m) setFixValue(m[1]);
    }
  }, [trip?.status, trip?.generation_error]);
  const [showChargers, setShowChargers] = useState(false);
  const [routeChargers, setRouteChargers] = useState(null);
  const [chargersLoading, setChargersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("reacties") ? "story" : "planning");
  const [openAllComments] = useState(() => new URLSearchParams(window.location.search).get("reacties") === "1");
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [tip, setTip] = useState(null);
  const [tipVisible, setTipVisible] = useState(false);
  const tipKey = `vr_tip_dismissed_${id}_${new Date().toISOString().slice(0, 10)}`;
  const dismissTip = () => { localStorage.setItem(tipKey, "1"); setTipVisible(false); };
  useEffect(() => {
    if (localStorage.getItem(tipKey)) return;
    let t;
    api.get(`/trips/${id}/assistant/tip`).then((r) => {
      if (r.data?.tip) {
        setTip(r.data.tip);
        t = setTimeout(() => setTipVisible(true), 1800);
      }
    }).catch(() => {});
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const [importDay, setImportDay] = useState(null);
  const [gpxPreview, setGpxPreview] = useState(null);
  const [gpxBusy, setGpxBusy] = useState(false);
  const importInputRef = useRef(null);
  const photobookAutoRef = useRef(false);
  const { theme } = useTheme();
  const seenKey = `vr_comments_seen_${id}`;
  const guestComments = useMemo(() => (trip?.comments || []).filter((c) => !c.is_owner), [trip?.comments]);
  const newComments = useMemo(() => guestComments.filter((c) => c.created_at > (localStorage.getItem(seenKey) || "")).length, [guestComments, seenKey]);

  useEffect(() => {
    if (activeTab !== "story" || guestComments.length === 0) return;
    const latest = guestComments.reduce((m, c) => (c.created_at > m ? c.created_at : m), "");
    if (latest) localStorage.setItem(seenKey, latest);
  }, [activeTab, guestComments, seenKey]);

  const toggleChargers = async () => {
    if (showChargers) {
      setShowChargers(false);
      return;
    }
    setShowChargers(true);
    if (!routeChargers) {
      setChargersLoading(true);
      try {
        const { data } = await api.get("/chargers/route", { params: { trip_id: id } });
        setRouteChargers(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        setShowChargers(false);
      } finally {
        setChargersLoading(false);
      }
    }
  };

  const downloadPhotobook = async () => {
    const tid = toast.loading("Fotoboek samenstellen — even geduld...");
    try {
      const res = await api.get(`/trips/${id}/export/photobook`, { responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `voltroute-fotoboek-${(trip?.name || "trip").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Fotoboek gedownload! 📖", { id: tid });
    } catch (err) {
      let msg = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail; } catch { msg = null; }
      }
      toast.error(formatApiError(msg) || "Fotoboek maken mislukt", { id: tid });
    }
  };

  const pickGpxImport = (d) => { setImportDay(d); importInputRef.current?.click(); };

  useEffect(() => {
    if (!trip || photobookAutoRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("fotoboek") === "1") {
      photobookAutoRef.current = true;
      navigate(`/trip/${id}`, { replace: true });
      downloadPhotobook();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip]);

  const importGpx = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const tid = toast.loading("GPX-bestand lezen...");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/trips/${id}/import/gpx/preview${importDay != null ? `?day=${importDay}` : ""}`, fd);
      toast.dismiss(tid);
      setGpxPreview({ ...data, file: f });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail), { id: tid });
    }
  };

  const confirmGpxImport = async () => {
    setGpxBusy(true);
    const fd = new FormData();
    fd.append("file", gpxPreview.file);
    try {
      const { data } = await api.post(`/trips/${id}/import/gpx${gpxPreview.day != null ? `?day=${gpxPreview.day}` : ""}`, fd);
      toast.success(`GPX geïmporteerd — ${data.distance_km} km, ${data.charging_stops} laadstop(s) herberekend`);
      setGpxPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGpxBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/trips/${id}`);
      setTrip(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/dashboard");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (trip?.status !== "generating") return;
    const interval = setInterval(async () => {
      const t = await load();
      if (t && t.status !== "generating") {
        clearInterval(interval);
        if (t.status === "ready") toast.success("Je trip is klaar!");
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [trip?.status, load]);

  useEffect(() => {
    const m = trip?.status === "error" && (trip.generation_error || "").match(/'(.+?)' niet gevonden/);
    if (!m) { setSuggestions(null); return; }
    let alive = true;
    api.get("/geocode/suggest", { params: { q: m[1] } })
      .then(({ data }) => { if (alive) setSuggestions(data.suggestions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [trip?.status, trip?.generation_error]);

  useEffect(() => {
    if (!trip?.route || !["ready", "active", "done"].includes(trip?.status)) { setProgress(null); return; }
    let alive = true;
    api.get(`/trips/${id}/progress`).then(({ data }) => { if (alive) setProgress(data?.total_km ? data : null); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, trip]);

  useEffect(() => {
    if (!progress?.started || progress?.finished) return;
    const refetch = () => api.get(`/trips/${id}/progress`).then(({ data }) => setProgress((p) => (data?.total_km ? data : p))).catch(() => {});
    const t = setInterval(refetch, 60000);
    window.addEventListener("vr-progress-refresh", refetch);
    return () => { clearInterval(t); window.removeEventListener("vr-progress-refresh", refetch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, progress?.started, progress?.finished]);

  const regenerate = async () => {
    await api.post(`/trips/${id}/regenerate`);
    load();
  };

  const cancelGeneration = async () => {
    try {
      await api.post(`/trips/${id}/cancel-generation`);
      toast.success("Berekening geannuleerd");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setStatus = async (status) => {
    await api.patch(`/trips/${id}/status`, { status });
    load();
  };

  if (!trip) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (trip.status === "generating") {
    return (
      <div className="min-h-screen bg-background">
        <header className="glass-panel border-b border-border/60 h-16 flex items-center px-6">
          <button data-testid="trip-back-button" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-[color] duration-200">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
        </header>
        <GeneratingScreen trip={trip} onCancel={cancelGeneration} />
      </div>
    );
  }

  if (trip.status === "error") {
    const errMatch = (trip.generation_error || "").match(/(Vertrekpunt|Tussenbestemming|Bestemming|Overnachtingslocatie) '(.+?)' niet gevonden/);
    const kindMap = { Vertrekpunt: "origin", Tussenbestemming: "waypoint", Bestemming: "destination", Overnachtingslocatie: "stay" };
    const checkSpelling = async () => {
      setChecking(true);
      setCheckResult(null);
      try {
        const r = await api.get("/geocode", { params: { q: fixValue.trim() } });
        setCheckResult(r.data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setChecking(false);
      }
    };
    const applyFix = async (val) => {
      const newVal = (typeof val === "string" ? val : fixValue).trim();
      setFixing(true);
      try {
        await api.post(`/trips/${id}/fix-location`, { kind: kindMap[errMatch[1]], old: errMatch[2], new: newVal });
        toast.success("Locatie aangepast — de route wordt opnieuw berekend");
        setCheckResult(null);
        load();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setFixing(false);
      }
    };
    const cancelFix = async () => {
      setCanceling(true);
      try {
        await api.post(`/trips/${id}/cancel-location`, { kind: kindMap[errMatch[1]], old: errMatch[2] });
        toast.success("Wijziging geannuleerd — de vorige route wordt hersteld");
        load();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setCanceling(false);
      }
    };
    const canCancel = errMatch && ["waypoint", "stay"].includes(kindMap[errMatch[1]]);
    return (
      <div className="min-h-screen bg-background">
        <header className="glass-panel border-b border-border/60 h-16 flex items-center px-6">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-[color] duration-200">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
        </header>
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-10 text-center" data-testid="trip-error-screen">
          <h2 className="font-heading font-extrabold text-2xl mb-3">Er ging iets mis</h2>
          <p className="text-muted-foreground max-w-md mb-6">{trip.generation_error}</p>
          {errMatch && (
            <div className="w-full max-w-md bg-card border border-border rounded-2xl p-5 mb-6 text-left" data-testid="fix-location-card">
              {suggestions?.length > 0 && (
                <div className="mb-4" data-testid="fix-suggestions">
                  <p className="text-xs font-semibold text-muted-foreground mb-1.5">Bedoelde je:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map((s, i) => (
                      <button key={i} data-testid={`fix-suggestion-${i}`} disabled={fixing} onClick={() => applyFix(s.label)}
                        className="rounded-full bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 text-xs font-semibold px-3 py-1.5 transition-[background-color] duration-200 disabled:opacity-50">
                        {s.label}{s.region ? ` (${s.region})` : ""} →
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">Eén klik = aanpassen & route herberekenen</p>
                </div>
              )}
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Corrigeer de schrijfwijze van {errMatch[1] === "Vertrekpunt" ? "het vertrekpunt" : errMatch[1] === "Bestemming" ? "de bestemming" : errMatch[1] === "Tussenbestemming" ? "de tussenbestemming" : "de overnachtingslocatie"}
              </label>
              <div className="flex gap-2">
                <Input data-testid="fix-location-input" value={fixValue}
                  onChange={(ev) => { setFixValue(ev.target.value); setCheckResult(null); }}
                  onKeyDown={(ev) => ev.key === "Enter" && fixValue.trim().length >= 2 && checkSpelling()}
                  placeholder="bv. Eichhoffen, Frankrijk" className="rounded-xl" />
                <Button data-testid="fix-location-check" variant="outline" className="rounded-full shrink-0" onClick={checkSpelling}
                  disabled={checking || fixValue.trim().length < 2}>
                  {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Controleer"}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Tip: laat straat en huisnummer weg — gebruik "plaatsnaam, land".</p>
              {checkResult && (checkResult.found ? (
                <p data-testid="fix-location-found" className="text-xs text-[#2D6A4F] font-semibold mt-2">✓ Gevonden: {checkResult.name || checkResult.display_name || fixValue}</p>
              ) : (
                <p data-testid="fix-location-notfound" className="text-xs text-red-600 font-semibold mt-2">✗ Ook niet gevonden — probeer een andere schrijfwijze</p>
              ))}
              <Button data-testid="fix-location-apply" onClick={() => applyFix()} disabled={fixing || !checkResult?.found}
                className="rounded-full bg-primary hover:bg-[#1B4332] w-full mt-3">
                {fixing ? "Bezig..." : "Pas aan & herbereken de route"}
              </Button>
              {canCancel && (
                <Button data-testid="fix-location-cancel" variant="ghost" onClick={cancelFix} disabled={canceling}
                  className="rounded-full w-full mt-2 text-muted-foreground hover:text-foreground">
                  {canceling ? "Bezig..." : `Annuleer — verwijder "${errMatch[2].length > 40 ? errMatch[2].slice(0, 40) + "…" : errMatch[2]}" en herstel de route`}
                </Button>
              )}
            </div>
          )}
          <div className="flex gap-3">
            <Button data-testid="trip-retry-button" onClick={regenerate} className="rounded-full bg-primary hover:bg-[#1B4332]">
              <RefreshCw className="w-4 h-4 mr-2" /> Opnieuw proberen
            </Button>
            <Button data-testid="trip-delete-error-button" variant="outline" className="rounded-full" onClick={() => setConfirmDel(true)}>
              <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
            </Button>
          </div>
          {confirmDel && (
            <div className="w-full max-w-md bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mt-4 text-left" data-testid="delete-error-confirm">
              <p className="text-sm leading-relaxed">
                Je verwijdert de volledige trip <strong>"{trip.name}"</strong> — inclusief routeplanning, dagen, reislog en instellingen.
                De trip gaat 30 dagen naar de <strong>prullenbak</strong> op je dashboard en is daar te herstellen.
              </p>
              <div className="flex gap-2 mt-3">
                <Button data-testid="confirm-error-delete" className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                  onClick={async () => { await api.delete(`/trips/${id}`); toast.success("Trip naar de prullenbak verplaatst"); navigate("/dashboard"); }}>
                  Ja, naar prullenbak
                </Button>
                <Button variant="outline" className="rounded-full" data-testid="cancel-error-delete" onClick={() => setConfirmDel(false)}>Annuleer</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {liveMode && <LiveMode trip={trip} onExit={() => setLiveMode(false)} onRefresh={load} />}
      <GpxPreviewDialog preview={gpxPreview} trip={trip} dark={theme === "dark"} busy={gpxBusy}
        onConfirm={confirmGpxImport} onCancel={() => setGpxPreview(null)} />

      <header className="sticky top-0 z-40 glass-panel border-b border-border/60">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="trip-back-button" onClick={() => navigate("/dashboard")} className="p-2 rounded-full hover:bg-secondary transition-[background-color] duration-200 shrink-0">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <h1 className="font-heading font-extrabold text-lg tracking-tight truncate">{trip.name}</h1>
              <p className="text-xs text-muted-foreground truncate">
                {trip.origin?.name} → {trip.destination?.name} · <span className="font-mono">{trip.route?.distance_km} km</span> · {trip.days?.length} {trip.days?.length === 1 ? "dag" : "dagen"}
                {trip.travel_mode !== "moto" && (trip.route?.distance_km || 0) > 0 && (
                  <span data-testid="co2-badge-trip" title={`T.o.v. dezelfde reis met een benzineauto (±170 g CO₂/km) — evenveel als ${co2Trees(trip.route.distance_km)} ${co2Trees(trip.route.distance_km) === 1 ? "boom" : "bomen"} die een jaar lang CO₂ opnemen`}
                    className="inline-flex items-center gap-1 ml-1.5 text-[#2D6A4F] font-semibold">
                    · <Leaf className="w-3 h-3" /> <span className="font-mono">{co2SavedKg(trip.route.distance_km)} kg</span> CO₂ bespaard
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 min-w-0 overflow-x-auto hide-scrollbar [&>*]:shrink-0" data-testid="trip-action-bar">
            <ThemeToggle className="hidden sm:inline-flex" />
            {trip.is_owner !== false && (
              <Button data-testid="edit-trip-button" variant="outline" onClick={() => navigate(`/nieuwe-trip?edit=${id}`)}
                className="rounded-full active:scale-95 transition-[transform,background-color] duration-200" title="Planning aanpassen — verslagen en reacties blijven behouden">
                <Pencil className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Aanpassen</span>
              </Button>
            )}
            {trip.is_owner !== false && <MembersDialog trip={trip} onRefresh={load} />}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button data-testid="photobook-menu-button" variant="outline"
                  className="rounded-full active:scale-95 transition-[transform,background-color] duration-200" title="Fotoboek: bewerken, downloaden of laten drukken">
                  <BookOpen className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Fotoboek</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                <DropdownMenuItem data-testid="photobook-menu-edit" className="cursor-pointer font-semibold" onClick={() => navigate(`/trip/${id}/fotoboek`)}>
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Bewerken & voorbeeld
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="photobook-menu-download" className="cursor-pointer" onClick={downloadPhotobook}>
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
                  <span className="ml-auto text-[9px] font-bold bg-primary/10 text-primary rounded-full px-2 py-0.5">PRO</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="photobook-menu-order" className="cursor-pointer font-semibold text-[#B5838D]" onClick={() => orderPrintBook(id)}>
                  <Printer className="w-3.5 h-3.5 mr-1.5" /> Bestel als gedrukt boek
                  <span className="ml-auto text-[9px] font-bold bg-primary/10 text-primary rounded-full px-2 py-0.5">PRO</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button data-testid="gpx-export-button" variant="outline"
                  className="rounded-full active:scale-95 transition-[transform,background-color] duration-200" title="Download GPX voor je navigatie">
                  <Download className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">GPX</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl max-h-80 overflow-y-auto">
                <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Exporteer GPX</DropdownMenuLabel>
                <DropdownMenuItem data-testid="gpx-full-trip" className="cursor-pointer font-semibold"
                  onClick={() => window.open(`${API_BASE}/trips/${id}/export/gpx`, "_blank")}>
                  Volledige trip
                </DropdownMenuItem>
                {(trip.days || []).filter((d) => d.type !== "stay").map((d) => (
                  <DropdownMenuItem key={d.day_number} data-testid={`gpx-day-${d.day_number}`} className="cursor-pointer text-xs"
                    onClick={() => window.open(`${API_BASE}/trips/${id}/export/gpx?day=${d.day_number}`, "_blank")}>
                    Dag {d.day_number} — {(d.start?.name || "").split(",")[0]} → {(d.end?.name || "").split(",")[0]}
                  </DropdownMenuItem>
                ))}
                {trip.is_owner !== false && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Importeer eigen GPX-route</DropdownMenuLabel>
                    <DropdownMenuItem data-testid="gpx-import-full" className="cursor-pointer font-semibold" onClick={() => pickGpxImport(null)}>
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> Hele route vervangen
                    </DropdownMenuItem>
                    {(trip.days || []).filter((d) => d.type !== "stay" && (d.distance_km || 0) > 0).map((d) => (
                      <DropdownMenuItem key={`imp-${d.day_number}`} data-testid={`gpx-import-day-${d.day_number}`} className="cursor-pointer text-xs" onClick={() => pickGpxImport(d.day_number)}>
                        <Upload className="w-3 h-3 mr-1.5" /> Dag {d.day_number} — {(d.start?.name || "").split(",")[0]} → {(d.end?.name || "").split(",")[0]}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <input ref={importInputRef} type="file" accept=".gpx,application/gpx+xml" className="hidden" onChange={importGpx} data-testid="gpx-import-input" />
            <PosterDialog trip={trip} progress={progress} canCreateShare={trip.is_owner !== false} />
            {trip.is_owner !== false && <ShareDialog trip={trip} onRefresh={load} />}
            <Button
              data-testid="live-mode-toggle-button"
              onClick={() => { setLiveMode(true); if (trip.status === "ready") setStatus("active"); }}
              className="rounded-full bg-[#1B263B] hover:bg-[#0F172A] text-white active:scale-95 transition-[transform,background-color] duration-200"
            >
              <Play className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Live modus</span><span className="sm:hidden">Live</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-64px)]">
        <div className="h-[38vh] lg:h-auto lg:flex-1 lg:order-2 relative isolate">
          <TripMap trip={trip} selectedPoint={selectedPoint} dark={theme === "dark"} chargers={showChargers ? routeChargers || [] : []} progress={progress} progressCorner="bottom-right"
            editable={trip.is_owner !== false && ["ready", "active"].includes(trip.status)} onRefresh={load} />
          {trip.travel_mode !== "moto" && (
          <button data-testid="route-chargers-toggle" onClick={toggleChargers}
            className={`absolute top-3 right-3 z-[1000] rounded-full px-4 py-2 text-xs font-bold shadow-lg flex items-center gap-1.5 active:scale-95 transition-[transform,background-color,color] duration-200 ${showChargers ? "bg-primary text-primary-foreground" : "bg-white text-foreground hover:bg-secondary"}`}>
            <Zap className={`w-3.5 h-3.5 ${chargersLoading ? "animate-pulse" : ""}`} />
            {chargersLoading ? "Laders zoeken..." : showChargers ? `${routeChargers?.length || 0} laders langs route` : "Laders langs route"}
          </button>
          )}
        </div>

        <div className="lg:w-[520px] lg:order-1 overflow-y-auto hide-scrollbar p-4 sm:p-6">
          {trip.replan_warning && (
            <div data-testid="replan-warning-banner" className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold">{(trip.replan_warning || "").startsWith("Berekening geannuleerd") ? "Berekening geannuleerd — je vorige planning is teruggezet" : "Herplanning mislukt — je vorige planning is teruggezet"}</p>
                <p className="text-xs text-muted-foreground mt-1">{trip.replan_warning}</p>
                <Button data-testid="retry-replan-button" size="sm" onClick={regenerate}
                  className="mt-2.5 rounded-full h-8 px-4 text-xs bg-amber-600 hover:bg-amber-700 text-white">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Probeer opnieuw
                </Button>
              </div>
              <button data-testid="dismiss-replan-warning" title="Melding sluiten"
                onClick={async () => { await api.delete(`/trips/${id}/replan-warning`); load(); }}
                className="p-1 rounded-full hover:bg-amber-500/20 text-amber-700 transition-[background-color] duration-200">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {!healthDismissed && (routeHealth?.length || 0) > 0 && trip.is_owner !== false && (
            <div data-testid="route-health-banner" className="mb-4 rounded-2xl border border-violet-500/40 bg-violet-500/10 p-4 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold">De assistent ziet iets vreemds in je route</p>
                <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                  {routeHealth.map((i, n) => <li key={n} data-testid={`route-health-issue-${i.type}`}>• {i.message}</li>)}
                </ul>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  {routeHealth.some((i) => i.fixable) && (
                    <Button data-testid="route-health-fix-btn" size="sm" onClick={fixRouteHealth}
                      className="rounded-full h-8 px-4 text-xs bg-violet-600 hover:bg-violet-700 text-white">
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Herstel route
                    </Button>
                  )}
                  <Button data-testid="route-health-chat-btn" size="sm" variant="outline" onClick={() => setAssistantOpen(true)}
                    className="rounded-full h-8 px-4 text-xs border-violet-500/40 text-violet-700 hover:bg-violet-500/10">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Vraag de assistent
                  </Button>
                </div>
              </div>
              <button data-testid="route-health-dismiss" title="Melding sluiten" onClick={() => setHealthDismissed(true)}
                className="p-1 rounded-full hover:bg-violet-500/20 text-violet-700 transition-[background-color] duration-200">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full rounded-full mb-6 sticky top-0 z-10">
              <TabsTrigger data-testid="tab-planning" value="planning" className="rounded-full text-xs">
                <Route className="w-3.5 h-3.5 mr-1 sm:mr-1.5 shrink-0" /> Planning
              </TabsTrigger>
              <TabsTrigger data-testid="tab-budget" value="budget" className="rounded-full text-xs">
                <Wallet className="w-3.5 h-3.5 mr-1 sm:mr-1.5 shrink-0" /> Budget
              </TabsTrigger>
              <TabsTrigger data-testid="tab-packing" value="packing" className="rounded-full text-xs">
                <Luggage className="w-3.5 h-3.5 mr-1 sm:mr-1.5 shrink-0" /> Paklijst
              </TabsTrigger>
              <TabsTrigger data-testid="tab-story" value="story" className="rounded-full text-xs">
                <Camera className="w-3.5 h-3.5 mr-1 sm:mr-1.5 shrink-0" /> Verslag
                {activeTab !== "story" && newComments > 0 && (
                  <span data-testid="story-tab-badge" className="ml-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#B5838D] text-white text-[10px] font-bold inline-flex items-center justify-center">{newComments}</span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="planning">
              <ReplanSummaryBanner trip={trip} onRefresh={load} />
              {trip.has_plan_backup && trip.is_owner !== false && ["ready", "active"].includes(trip.status) && (
                <div className="mb-3 flex justify-end">
                  <button data-testid="restore-plan-button"
                    onClick={async () => {
                      if (!(await confirmDialog("De huidige planning wordt vervangen door de vorige versie (van vóór de laatste herberekening). Doorgaan?", { title: "Vorige planning terugzetten", confirmLabel: "Terugzetten" }))) return;
                      try {
                        const { data } = await api.post(`/trips/${id}/restore-plan`);
                        toast.success(`Vorige planning teruggezet (${data.days} dagen)`);
                        load();
                      } catch (err) {
                        toast.error(formatApiError(err.response?.data?.detail));
                      }
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline flex items-center gap-1.5 transition-colors duration-200">
                    <Undo2 className="w-3.5 h-3.5" /> Vorige planning terugzetten
                  </button>
                </div>
              )}
              {trip.travel_mode !== "moto" && (
                <ChargeCalcDialog trip={trip}>
                  <button data-testid="charge-calc-standalone-button"
                    className="w-full bg-card rounded-2xl mb-4 p-4 flex items-center gap-2.5 text-left hover:bg-secondary/50 transition-[background-color] duration-200">
                    <Calculator className="w-4 h-4 text-primary shrink-0" />
                    <span className="font-heading font-bold text-sm flex-1">Laad-rekenaar</span>
                    <span className="text-xs text-muted-foreground font-mono">kW → km/u</span>
                  </button>
                </ChargeCalcDialog>
              )}
              <GuestSuggestions trip={trip} onRefresh={load} />
              <VehicleCard trip={trip} onRefresh={load} />
              <ImportantInfo trip={trip} onRefresh={load} countries={countryData?.countries} />
              {trip.is_owner !== false && trip.travel_mode !== "moto" && <BatteryStartCard trip={trip} onRefresh={load} />}
              <Timeline trip={trip} onSelectPoint={setSelectedPoint} onRefresh={load} dayEntries={countryData?.day_entries} />
            </TabsContent>

            <TabsContent value="budget">
              <CurrencyConverter countries={countryData?.countries} />
              <BudgetTab trip={trip} onRefresh={load} />
              <SplitCosts trip={trip} onRefresh={load} />
            </TabsContent>

            <TabsContent value="packing">
              <PackingList trip={trip} />
            </TabsContent>

            <TabsContent value="story">
              <PrintOrderButton tripId={trip.id} variant="card" />
              <CostRecap trip={trip} progress={progress} />
              <TripStory trip={trip} onRefresh={load} onSelectPoint={setSelectedPoint} openAllComments={openAllComments} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {!liveMode && (
        <>
          {tipVisible && tip && !assistantOpen && (
            <div data-testid="assistant-tip-bubble" role="button"
              onClick={() => { setAssistantOpen(true); dismissTip(); }}
              className="fixed bottom-6 right-24 z-[1200] max-w-[280px] bg-card border border-border rounded-2xl rounded-br-sm shadow-xl p-3.5 pr-9 cursor-pointer animate-in fade-in slide-in-from-bottom-2 duration-500">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#2D6A4F] mb-1">Tip van Volt</p>
              <p className="text-sm leading-snug">{tip}</p>
              <button data-testid="assistant-tip-close" onClick={(e) => { e.stopPropagation(); dismissTip(); }}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {assistantOpen && (
            <div data-testid="assistant-panel" className="fixed bottom-24 right-4 z-[1200] w-[min(400px,calc(100vw-2rem))] rounded-2xl shadow-2xl border border-border overflow-hidden">
              <AssistantChat tripId={trip.id} canApply={trip.is_owner !== false} onApplied={load} />
            </div>
          )}
          <button data-testid="assistant-bubble" onClick={() => setAssistantOpen((o) => !o)} title="Vraag het aan je AI-reisassistent"
            className={`fixed bottom-6 right-4 z-[1200] w-16 h-16 rounded-full shadow-[0_8px_30px_rgba(45,106,79,0.45)] flex items-center justify-center hover:scale-110 active:scale-95 transition-[transform] duration-200 ${assistantOpen ? "bg-primary text-primary-foreground" : "bg-white vr-avatar-bob"}`}>
            {assistantOpen
              ? <X className="w-6 h-6" />
              : (
                <span className="relative">
                  <img src="/volt-avatar.png" alt="AI-reisassistent" className="w-16 h-16 rounded-full" />
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#52B788] border-2 border-white animate-pulse" />
                </span>
              )}
          </button>
        </>
      )}
    </div>
  );
}
